/* eslint-disable react/prop-types */
import { getHistoricosMovimientosEmpleados, getHistoricoMovimientosEmpleadosPlantilla } from 'api/nominas/historicoMovimientosEmpleados';
import { checkToken } from 'api/utils';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Dragger from 'components/Dragger';
import React, { useEffect, useState } from 'react';
import currencyFormatter from 'utils/currencyFormatter';
import { formatReceived } from 'utils/formatValues';
import { getModelPermission } from 'utils/permission';
import { onError, onSuccess } from 'utils/handlers';
import HTTP, { baseURL } from 'utils/api';
import {
  Menu,
  Row,
  Modal,
  Form,
  Spin,
} from 'antd';
import {
  FileExcelOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

export const HistoricoMovimientoEmpleadoPermission = {
  permissionModel: 'historicomovimientoempleado',
  catalogsModels: [],
  tabsModels: [],
};

const baseURI = 'nominas/movimientos-empleados-importar-excel';

const ExpedienteMovimientosEmpleado = ({
  currentEmpleado,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleModal, setVisibleModal] = useState(false);
  const [formCarga] = Form.useForm();

  const getPermission = getModelPermission(
    HistoricoMovimientoEmpleadoPermission.permissionModel,
    HistoricoMovimientoEmpleadoPermission.catalogsModels,
    HistoricoMovimientoEmpleadoPermission.tabsModels,
    false,
  );

  const fetchData = async () => {
    setLoading(true);
    const _data = await getHistoricosMovimientosEmpleados({ empleado: currentEmpleado });
    setData(formatReceived(_data));
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _data,
      ] = await Promise.all([
        getHistoricosMovimientosEmpleados({ empleado: currentEmpleado }),
      ]);
      if (mounted) {
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    if (currentEmpleado) {
      fetchAll();
    } else {
      setData([]);
      setLoading(false);
    }
    return () => { mounted = false; };
  }, [currentEmpleado]);

  const onCancel = () => {
    setVisibleModal(false);
    formCarga.resetFields();
  };

  const onClickObtenerPlantilla = async () => {
    try {
      setLoading(true);
      const excel = await getHistoricoMovimientosEmpleadosPlantilla();
      if (excel) {
        const blob = new Blob([excel], {
          type: 'application/ms-excel',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'HistoricoMovimientoEmpleado_Plantilla.xlsx';
        link.click();
      }
      setLoading(false);
    } catch (err) {
      onError(err);
    }
  };

  const onClickCargar = () => {
    setVisibleModal(true);
  };

  const onUpload = async () => {
    try {
      await formCarga.validateFields();
      const { file } = formCarga.getFieldsValue();
      if (file) {
        setLoading(true);
        const formData = new FormData();
        formData.append('archivo', file.file);
        const uri = baseURI.at(-1) === '/' ? baseURI : `${baseURI}/`;
        const response = await HTTP.post(`${baseURL}${uri}`, formData, {
          headers: {
            'Content-Type': 'application/ms-excel',
          },
          responseType: 'arraybuffer',
        });
        if (response?.data) {
          const blob = new Blob([response?.data], {
            type: 'application/ms-excel',
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'HistoricoMovimientoEmpleado_Respuesta.xlsx';
          link.click();
        }
        if (response?.status === 200) {
          onSuccess(response, 'Operación realizada correctamente', 5);
          await fetchData();
          onCancel();
        }
      }
      setLoading(false);
      setVisibleModal(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const menuImport = (
    <Menu
      items={[
        {
          key: 1,
          label: 'Obtener Plantilla',
          text: 'Obtener Plantilla',
          icon: <FileExcelOutlined />,
          onClick: () => onClickObtenerPlantilla(),
        },
        {
          key: 2,
          label: 'Cargar',
          text: 'Cargar',
          icon: <DownloadOutlined />,
          onClick: () => onClickCargar(),
        },
      ]}
    />
  );

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  const columns = [
    {
      titleText: 'No. de Movimiento',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Fecha Captura',
      dataIndex: 'fecha_de_captura',
      key: 'fecha_de_captura',
      width: 200,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha Ejecución',
      dataIndex: 'fecha_de_ejecucion',
      key: 'fecha_de_ejecucion',
      width: 200,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'Tipo de Movimiento',
      dataIndex: 'tipo_de_movimiento_descripcion',
      key: 'tipo_de_movimiento_descripcion',
      width: 200,
    },
    {
      titleText: 'Motivo',
      dataIndex: 'motivo_de_movimiento',
      key: 'motivo_de_movimiento',
      width: 300,
    },
    {
      titleText: 'No. Plaza Anterior',
      dataIndex: 'plaza_anterior',
      key: 'plaza_anterior',
      width: 200,
    },
    {
      titleText: 'No. Plaza Nueva',
      dataIndex: 'plaza_nueva',
      key: 'plaza_nueva',
      width: 200,
    },
    {
      titleText: 'Área Anterior',
      dataIndex: 'area_anterior',
      key: 'area_anterior',
      width: 200,
    },
    {
      titleText: 'Área Nueva',
      dataIndex: 'area_nueva',
      key: 'area_nueva',
      width: 200,
    },
    {
      titleText: 'Puesto Anterior',
      dataIndex: 'puesto_anterior',
      key: 'puesto_anterior',
      width: 200,
    },
    {
      titleText: 'Puesto Nuevo',
      dataIndex: 'puesto_nuevo',
      key: 'puesto_nuevo',
      width: 200,
    },
    {
      titleText: 'Categoría de Puesto Anterior',
      dataIndex: 'categoria_puesto_anterior',
      key: 'categoria_puesto_anterior',
      width: 250,
    },
    {
      titleText: 'Categoría de Puesto Nueva',
      dataIndex: 'categoria_puesto_nuevo',
      key: 'categoria_puesto_nuevo',
      width: 250,
    },
    {
      titleText: 'Sueldo Anterior',
      dataIndex: 'sueldo_diario_anterior',
      key: 'sueldo_diario_anterior',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Sueldo Nuevo',
      dataIndex: 'sueldo_diario_nuevo',
      key: 'sueldo_diario_nuevo',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Tipo de Empleado Anterior',
      dataIndex: 'tipo_de_empleado_anterior',
      key: 'tipo_de_empleado_anterior',
      width: 250,
    },
    {
      titleText: 'Tipo de Empleado Nuevo',
      dataIndex: 'tipo_de_empleado_nuevo',
      key: 'tipo_de_empleado_nuevo',
      width: 250,
    },
    {
      titleText: 'Tipo de Función Anterior',
      dataIndex: 'tipo_de_funcion_anterior',
      key: 'tipo_de_funcion_anterior',
      width: 200,
    },
    {
      titleText: 'Tipo de Función Nueva',
      dataIndex: 'tipo_de_funcion_nueva',
      key: 'tipo_de_funcion_nueva',
      width: 200,
    },
    {
      titleText: 'Fecha de Alta',
      dataIndex: 'fecha_de_alta',
      key: 'fecha_de_alta',
      width: 200,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha de Baja',
      dataIndex: 'fecha_de_baja',
      key: 'fecha_de_baja',
      width: 200,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha de Reingreso',
      dataIndex: 'fecha_de_reingreso',
      key: 'fecha_de_reingreso',
      width: 200,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          permission={getPermission}
          allowCustomImport
          customImportMenu={menuImport}
          customImportText="Importar Movimientos Empleados"
          allowExportPDF={false}
          loading={loading}
        />
        <Modal
          open={visibleModal}
          title={(
            <FormSubmitControls
              label="Carga Masiva"
              onFinish={onUpload}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          footer={null}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          forceRender
          destroyOnClose
        >
          <Spin tip="Cargando..." spinning={loading}>
            <Form
              layout="vertical"
              form={formCarga}
              name="carga"
              onFinish={onUpload}
            >
              <Dragger
                label="Archivo"
                limitMB={30}
                rules={rules.required}
                name="file"
                allowedExtensions={['xlsx']}
              />
            </Form>
          </Spin>
        </Modal>
      </Spin>
    </Row>
  );
};

export default ExpedienteMovimientosEmpleado;
